import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { useMemo, useState } from 'react';
import { type DateRange, DayPicker, type PropsBase, type PropsMulti, type PropsRange, type PropsSingle } from 'react-day-picker';
import { useIntl } from 'react-intl';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import ArrowSq from '../../../icons/ArrowSq';
import ChevronSm from '../../../icons/ChevronSm';
import DayButton from './DayButton';
import { getDisabledProps } from './getDisabledProps';
import getLabels from './getLabels';
import getLocale from './getLocale';
import getRangeModifiers from './getRangeModifiers';
import isSelectedDateEmpty from './isSelectedDateEmpty';
import Root from './Root';
import styles from './styles/styles.module.scss';

// We use a custom onSelect so that we can call this function from the DatePicker clear button, where we don't have the extra info from react-day-picker
type SingleCalendarProps = Pick<PropsSingle, 'mode' | 'selected'> & { onSelect: (day?: Date) => void };
type MultipleCalendarProps = Pick<PropsMulti, 'mode' | 'selected'> & { onSelect: (days?: Date[]) => void };
type RangeCalendarProps = Pick<PropsRange, 'mode' | 'selected'> & { onSelect: (days?: DateRange) => void };

type CalendarProps = {
  languageRegionCode: LanguageRegionCode;
  validDates?: { from?: Date; to?: Date };
} & (SingleCalendarProps | MultipleCalendarProps | RangeCalendarProps);

const Calendar = ({ languageRegionCode, mode, onSelect, selected, validDates }: CalendarProps) => {
  const intl = useIntl();
  const [potentialDate, setPotentialDate] = useState<Date>();
  const debouncedSetPotentialDate = debounce(setPotentialDate, 50); // Avoid occasional style flashing when the hover lands on a grid border

  const today = useMemo(() => {
    if (mode === 'single' && selected) {
      return selected;
    }
    if (mode === 'range' && selected?.from) {
      return selected?.from;
    }

    return undefined;
  }, [mode, selected]);

  const dayPickerProps: PropsBase = {
    classNames: {
      button_next: classNames(styles.button, styles.button_next),
      button_previous: classNames(styles.button, styles.button_previous),
      day: styles.day,
      day_button: styles.day_button,
      disabled: styles.disabled,
      month_caption: styles.month_caption,
      month_grid: styles.month_grid,
      months: styles.months,
      range_start: styles.range_start,
      range_middle: styles.range_middle,
      range_end: styles.range_end,
      root: styles.root,
      selected: styles.selected,
      weekday: styles.weekday
    },
    components: {
      DayButton,
      Dropdown: () => <ChevronSm color="currentColor" orientation="down" size="sm" />,
      Chevron: props => {
        if (props.orientation === 'left') {
          return <ArrowSq color="currentColor" orientation="left" size="sm" />;
        }
        return <ArrowSq color="currentColor" orientation="right" size="sm" />;
      },
      Root
    },
    ...getDisabledProps(validDates),
    labels: getLabels(intl),
    locale: getLocale(languageRegionCode),
    navLayout: 'around',
    numberOfMonths: 1,
    pagedNavigation: false,
    today
  };

  if (mode === 'single') {
    return (
      // Only pass the date to the onSelect handler. The other arguments are discarded.
      <DayPicker {...dayPickerProps} mode={mode} onSelect={(single, _triggerDate, _modifiers, _event) => onSelect(single)} selected={selected} />
    );
  }

  if (mode === 'range') {
    return (
      <DayPicker
        {...dayPickerProps}
        mode={mode}
        modifiers={selected && potentialDate ? getRangeModifiers(selected, potentialDate) : undefined}
        onDayFocus={day => debouncedSetPotentialDate(day)}
        onDayBlur={() => debouncedSetPotentialDate(undefined)}
        onDayMouseEnter={day => debouncedSetPotentialDate(day)}
        onDayMouseLeave={() => debouncedSetPotentialDate(undefined)}
        onSelect={(range, triggerDate, _modifiers, _event) => {
          let rangeToSelect = range;
          // By default, if you already have a selected range and click on a new day, it uses the previous selected range to determine the new range.
          // We want to override this behavior so that it clears the old selection and you must select a new range "end".
          if (isSelectedDateEmpty(selected) || (selected?.from && selected.to)) {
            rangeToSelect = { from: triggerDate, to: undefined };
          }
          // Only pass the date range to the onSelect handler. The other arguments are discarded.
          onSelect?.(rangeToSelect);
        }}
        selected={selected}
      />
    );
  }

  return null;
};

export default Calendar;
